/* ==========================================================================
   Akmas theme behaviour (vanilla JS, no framework)
   - Announcement rotation + pausable marquee for long messages
   - Sticky-header shadow
   - Desktop mega menu (keyboard + click; hover handled in CSS)
   - Mobile drawer menu (scroll lock, focus trap, Escape, accordions)
   - Banner carousel (autoplay, swipe, dots, arrows, reduced-motion)
   - Product-card scent chips kept on one non-wrapping row (fitCardNotes)
   - Sticky Diwali box bar: scroll-gated, dismissible for the session
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Announcement bar
     Short plain-text offers fade one at a time (every 4s) on every screen.
     The bar has a fixed height and never wraps, so the header does not move.
     Rotation pauses on hover, touch and keyboard focus, stops when the tab is
     hidden, and is disabled (first message only, no animation) for
     prefers-reduced-motion.
     --------------------------------------------------------------------- */
  function initAnnouncement() {
    document.querySelectorAll('[data-ak-announcement]').forEach(function (bar) {
      if (bar.dataset.announcementReady === 'true') return;
      bar.dataset.announcementReady = 'true';

      var track = bar.querySelector('[data-ak-announcement-track]');
      if (!track) return;

      var items = Array.prototype.slice.call(track.querySelectorAll('[data-ak-announcement-item]'));
      if (!items.length) return;

      /* Reduced motion: keep the first message, never change it. */
      if (reduceMotion) {
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === 0);
        });
        return;
      }

      /* A single message has nothing to rotate to. */
      if (items.length < 2) return;

      var interval = parseInt(bar.dataset.interval, 10) || 4000;
      var index = 0;
      var timer = null;
      var paused = false;

      function show(next) {
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === next);
        });
        index = next;
      }

      function advance() {
        show((index + 1) % items.length);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      function start() {
        if (paused || document.hidden) return;
        stop();
        timer = window.setInterval(advance, interval);
      }

      function pause() {
        paused = true;
        stop();
      }

      function resume() {
        paused = false;
        start();
      }

      bar.addEventListener('mouseenter', pause);
      bar.addEventListener('mouseleave', resume);
      bar.addEventListener('focusin', pause);
      bar.addEventListener('focusout', resume);
      /* Pause while the bar is being touched (tap on a phone). */
      bar.addEventListener('touchstart', pause, { passive: true });
      bar.addEventListener('touchend', resume, { passive: true });

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      show(0);
      start();
    });
  }

  /* ---------------------------------------------------------------------
     Sticky header shadow
     --------------------------------------------------------------------- */
  function initStickyShadow() {
    var headers = document.querySelectorAll('[data-ak-header]');
    if (!headers.length) return;

    var ticking = false;
    function update() {
      var scrolled = window.scrollY > 8;
      headers.forEach(function (h) {
        h.classList.toggle('is-scrolled', scrolled);
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------------------------
     Desktop mega menu
     --------------------------------------------------------------------- */
  function initMegaMenu() {
    var nav = document.querySelector('[data-ak-nav]');
    if (!nav) return;

    var toggles = Array.prototype.slice.call(nav.querySelectorAll('[data-ak-mega-toggle]'));
    if (!toggles.length) return;

    function closeAll(except) {
      toggles.forEach(function (btn) {
        if (btn === except) return;
        btn.setAttribute('aria-expanded', 'false');
        var item = btn.closest('[data-ak-nav-item]');
        if (item) item.classList.remove('is-open');
      });
    }

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        closeAll(btn);
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var item = btn.closest('[data-ak-nav-item]');
        if (item) item.classList.toggle('is-open', !expanded);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = nav.querySelector('[data-ak-mega-toggle][aria-expanded="true"]');
      if (!open) return;
      closeAll(null);
      open.focus();
    });

    document.addEventListener('click', function (event) {
      if (nav.contains(event.target)) return;
      closeAll(null);
    });
  }

  /* ---------------------------------------------------------------------
     Mobile drawer menu
     --------------------------------------------------------------------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-ak-drawer]');
    if (!drawer) return;
    if (drawer.dataset.drawerReady === 'true') return;
    drawer.dataset.drawerReady = 'true';

    var panel = drawer.querySelector('[data-ak-drawer-panel]');
    if (!panel) return;

    var triggers = Array.prototype.slice.call(document.querySelectorAll('[data-ak-drawer-open]'));
    var staticAreas = Array.prototype.slice
      .call(panel.children)
      .filter(function (el) {
        return !el.hasAttribute('data-ak-subpanel');
      });
    var lastFocused = null;
    var activeSubpanel = null;

    function focusables() {
      return Array.prototype.slice
        .call(
          panel.querySelectorAll(
            'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          if (el.closest('[hidden]') || el.closest('[inert]')) return false;
          var sp = el.closest('[data-ak-subpanel]');
          if (sp && !sp.classList.contains('is-active')) return false;
          return el.offsetParent !== null;
        });
    }

    function open(trigger) {
      lastFocused = trigger || document.activeElement;
      drawer.hidden = false;
      document.body.classList.add('ak-no-scroll');
      document.body.classList.add('ak-drawer-open');
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
      // rAF so the transition runs after display is applied
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          drawer.classList.add('is-open');
        });
      });
      var closeBtn = drawer.querySelector('[data-ak-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      closeSubpanel(true);
      drawer.classList.remove('is-open');
      document.body.classList.remove('ak-no-scroll');
      document.body.classList.remove('ak-drawer-open');
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      var done = function () {
        drawer.hidden = true;
        drawer.removeEventListener('transitionend', done);
      };
      if (reduceMotion) {
        done();
      } else {
        drawer.addEventListener('transitionend', done);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    function syncSubTriggers() {
      panel.querySelectorAll('[data-ak-subpanel-open]').forEach(function (btn) {
        var isOpen = !!activeSubpanel && btn.getAttribute('data-ak-subpanel-open') === activeSubpanel.id;
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    function openSubpanel(btn) {
      var subpanel = document.getElementById(btn.getAttribute('data-ak-subpanel-open'));
      if (!subpanel || activeSubpanel === subpanel) return;
      if (activeSubpanel) closeSubpanel(true);

      activeSubpanel = subpanel;
      subpanel.hidden = false;
      staticAreas.forEach(function (el) {
        el.setAttribute('inert', '');
      });
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          subpanel.classList.add('is-active');
        });
      });
      var back = subpanel.querySelector('[data-ak-subpanel-close]');
      if (back) back.focus();
      syncSubTriggers();
    }

    function closeSubpanel(immediate) {
      var subpanel = activeSubpanel;
      if (!subpanel) return;
      activeSubpanel = null;

      staticAreas.forEach(function (el) {
        el.removeAttribute('inert');
      });
      subpanel.classList.remove('is-active');
      var done = function () {
        subpanel.hidden = true;
        subpanel.removeEventListener('transitionend', done);
      };
      if (immediate || reduceMotion) {
        done();
      } else {
        subpanel.addEventListener('transitionend', done);
      }
      syncSubTriggers();
      if (!immediate) {
        var btn = panel.querySelector('[data-ak-subpanel-open="' + subpanel.id + '"]');
        if (btn) btn.focus();
      }
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        open(trigger);
      });
    });

    drawer.querySelectorAll('[data-ak-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    // Buttons that open another overlay (search, cart) also dismiss the menu.
    panel.querySelectorAll('[data-ak-drawer-dismiss]').forEach(function (el) {
      el.addEventListener('click', function () {
        close();
      });
    });

    panel.querySelectorAll('[data-ak-subpanel-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openSubpanel(btn);
      });
    });

    panel.querySelectorAll('[data-ak-subpanel-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeSubpanel();
      });
    });

    // Tapping any link dismisses the menu (navigation proceeds regardless).
    panel.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (link) close();
    });

    document.addEventListener('keydown', function (event) {
      if (drawer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (activeSubpanel) closeSubpanel();
        else close();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Banner carousel (Akmas hero)
     92% slides on a flex track so the next banner peeks. 5s autoplay pauses
     on hover, touch, focus and hidden tab. Swipe on touch, arrows on desktop,
     continuous looping. No dots. Autoplay never runs for reduced motion.
     --------------------------------------------------------------------- */
  function initCarousel() {
    document.querySelectorAll('[data-ak-carousel]').forEach(function (root) {
      if (root.dataset.carouselReady === 'true') return;
      root.dataset.carouselReady = 'true';

      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-ak-slide]'));
      var track = root.querySelector('[data-ak-track]');
      if (slides.length < 2 || !track) return;

      var prevBtn = root.querySelector('[data-ak-prev]');
      var nextBtn = root.querySelector('[data-ak-next]');
      var status = root.querySelector('[data-ak-status]');
      var viewport = root.querySelector('[data-ak-viewport]');
      var autoplayEnabled = root.getAttribute('data-ak-autoplay') !== 'false';

      var AUTOPLAY_MS = 5000;
      var SWIPE_THRESHOLD = 45;
      var interval = null;
      var index = 0;

      slides.forEach(function (slide, i) {
        if (slide.classList.contains('is-active')) index = i;
      });

      var paused = { hover: false, touch: false, focus: false, hidden: document.hidden };

      function moveTrack() {
        var offset = slides[index].offsetLeft - slides[0].offsetLeft;
        track.style.transform = 'translate3d(' + -offset + 'px,0,0)';
      }

      function render() {
        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.classList.toggle('is-active', active);

          if (active) {
            slide.removeAttribute('aria-hidden');
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('aria-hidden', 'true');
            slide.setAttribute('inert', '');
          }

          var link = slide.querySelector('a');
          if (link) {
            if (active) link.removeAttribute('tabindex');
            else link.setAttribute('tabindex', '-1');
          }
        });

        moveTrack();
        if (status) status.textContent = index + 1 + ' of ' + slides.length;
      }

      function goTo(nextIndex, manual) {
        index = (((nextIndex % slides.length) + slides.length) % slides.length);

        // Announce manual moves politely; autoplay keeps the region silent.
        if (status) status.setAttribute('aria-live', manual ? 'polite' : 'off');
        render();

        if (manual) {
          stop();
          start();
        }
      }

      function shouldAutoplay() {
        return (
          autoplayEnabled &&
          !reduceMotion &&
          !paused.hover &&
          !paused.touch &&
          !paused.focus &&
          !paused.hidden
        );
      }

      function stop() {
        if (interval) {
          window.clearInterval(interval);
          interval = null;
        }
      }

      function start() {
        if (interval || !shouldAutoplay()) return;
        interval = window.setInterval(function () {
          if (status) status.setAttribute('aria-live', 'off');
          goTo(index + 1, false);
        }, AUTOPLAY_MS);
      }

      function evaluate() {
        if (shouldAutoplay()) start();
        else stop();
      }

      /* ---- Arrows ---- */
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(index - 1, true);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(index + 1, true);
        });
      }

      /* ---- Keyboard ---- */
      root.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(index - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(index + 1, true);
        }
      });

      /* ---- Pause on hover / focus ---- */
      root.addEventListener('mouseenter', function () {
        paused.hover = true;
        evaluate();
      });
      root.addEventListener('mouseleave', function () {
        paused.hover = false;
        evaluate();
      });
      root.addEventListener('focusin', function () {
        paused.focus = true;
        evaluate();
      });
      root.addEventListener('focusout', function (event) {
        if (event.relatedTarget && root.contains(event.relatedTarget)) return;
        paused.focus = false;
        evaluate();
      });

      /* ---- Pause on touch + swipe ---- */
      var touchStartX = null;
      var touchStartY = null;

      if (viewport) {
        viewport.addEventListener(
          'touchstart',
          function (event) {
            var touch = event.changedTouches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            paused.touch = true;
            evaluate();
          },
          { passive: true }
        );

        viewport.addEventListener(
          'touchend',
          function (event) {
            paused.touch = false;
            evaluate();

            if (touchStartX === null) return;
            var touch = event.changedTouches[0];
            var dx = touch.clientX - touchStartX;
            var dy = touch.clientY - touchStartY;
            touchStartX = null;
            touchStartY = null;

            if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
              goTo(index + (dx < 0 ? 1 : -1), true);
            }
          },
          { passive: true }
        );

        viewport.addEventListener(
          'touchcancel',
          function () {
            paused.touch = false;
            evaluate();
          },
          { passive: true }
        );
      }

      /* ---- Pause while the tab is hidden ---- */
      document.addEventListener('visibilitychange', function () {
        paused.hidden = document.hidden;
        evaluate();
      });

      /* ---- Respect a runtime change to prefers-reduced-motion ---- */
      if (window.matchMedia) {
        var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        var onMotionChange = function (event) {
          reduceMotion = event.matches;
          evaluate();
        };
        if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
        else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
      }

      window.addEventListener('resize', moveTrack);
      render();
      start();
    });
  }

  /* ---------------------------------------------------------------------
     Footer accordions (Shop / Help / Policies)
     The <details> groups are closed by default on mobile. From 990px up the
     same markup is forced open and its summary is made inert, so the desktop
     columns behave like static headings and can never be collapsed.
     --------------------------------------------------------------------- */
  function initFooterAccordions() {
    var groups = Array.prototype.slice.call(document.querySelectorAll('[data-ak-footer-group]'));
    if (!groups.length) return;

    var desktop = window.matchMedia('(min-width: 990px)');

    function apply() {
      groups.forEach(function (group) {
        var summary = group.querySelector('summary');
        if (desktop.matches) {
          group.setAttribute('open', '');
          if (summary) summary.inert = true;
        } else {
          group.removeAttribute('open');
          if (summary) summary.inert = false;
        }
      });
    }

    // Guard for browsers without inert: cancel activation while columns are static.
    groups.forEach(function (group) {
      group.addEventListener('click', function (event) {
        var target = event.target;
        if (desktop.matches && target && target.closest && target.closest('summary')) {
          event.preventDefault();
        }
      });
    });

    apply();

    if (desktop.addEventListener) desktop.addEventListener('change', apply);
    else if (desktop.addListener) desktop.addListener(apply);
  }

  /* ---------------------------------------------------------------------
     Product card quick "Add to cart"
     Uses the existing public AkmasCart.addItems API, then opens the drawer —
     no cart/offer logic is changed here.
     --------------------------------------------------------------------- */
  function initCardAdd() {
    if (document.body.dataset.akCardAdd === 'true') return;
    document.body.dataset.akCardAdd = 'true';

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-ak-add]');
      if (!btn || btn.disabled) return;
      event.preventDefault();

      var id = btn.getAttribute('data-ak-add');
      if (!id || !window.AkmasCart) return;

      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Adding…';

      window.AkmasCart.addItems([{ id: id, quantity: 1 }])
        .then(function () {
          btn.classList.add('is-added');
          btn.textContent = 'Added to cart';
          clearTimeout(btn.akAddedTimer);
          btn.akAddedTimer = setTimeout(function () {
            btn.classList.remove('is-added');
            btn.textContent = btn.getAttribute('data-ak-add-label') || 'Add to cart';
          }, 1500);
          var drawer = document.querySelector('akmas-cart-drawer');
          if (drawer) drawer.open(btn);
        })
        .catch(function (error) {
          console.warn('[akmas-card] add failed', error);
          btn.textContent = btn.getAttribute('data-ak-add-label') || 'Add to cart';
        })
        .finally(function () {
          btn.removeAttribute('aria-busy');
        });
    });
  }

  /* ---------------------------------------------------------------------
     Product tabs (Trending perfumes, Shop by gender)
     All panels render server-side; switching is show/hide only. Roving
     tabindex + arrow keys, aria-selected, and a live "View all" target.
     --------------------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('[data-ak-tabs]').forEach(function (root) {
      if (root.dataset.tabsReady === 'true') return;
      root.dataset.tabsReady = 'true';

      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ak-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ak-panel]'));
      var viewAll = root.querySelector('[data-ak-tabs-viewall]');
      if (tabs.length < 2) return;

      function select(index, focus) {
        tabs.forEach(function (tab, i) {
          var active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
        });
        panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
        });
        var panel = panels[index];
        if (viewAll && panel) {
          var url = panel.getAttribute('data-ak-tabs-url');
          if (url) viewAll.setAttribute('href', url);
        }
        if (focus) tabs[index].focus();

        // Keep the active pill in view inside the scrolling tablist.
        if (tabs[index] && tabs[index].scrollIntoView) {
          tabs[index].scrollIntoView({
            block: 'nearest',
            inline: 'center',
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
        // Re-sync the rail arrows inside the now-visible panel (its rail had
        // zero width while hidden, so the arrows start in a stale state).
        window.dispatchEvent(new Event('resize'));
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i, false);
        });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          select(next, true);
        });
      });

      select(0, false);
    });
  }

  /* ---------------------------------------------------------------------
     Horizontal rails with arrow buttons (image-only promo cards)
     Arrows scroll the rail by one card and disable at the ends.
     --------------------------------------------------------------------- */
  function initRail() {
    document.querySelectorAll('[data-ak-rail-root]').forEach(function (root) {
      if (root.dataset.railReady === 'true') return;
      root.dataset.railReady = 'true';

      var rail = root.querySelector('[data-ak-rail]');
      var prev = root.querySelector('[data-ak-rail-prev]');
      var next = root.querySelector('[data-ak-rail-next]');
      if (!rail || (!prev && !next)) return;

      function step() {
        var item = rail.querySelector(':scope > li');
        if (!item) return rail.clientWidth;
        var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 1;
        if (prev) prev.disabled = rail.scrollLeft <= 1;
        if (next) next.disabled = rail.scrollLeft >= max;
      }

      function go(dir) {
        rail.scrollBy({ left: dir * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      rail.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ---------------------------------------------------------------------
     Reels — 9:16 video rail
     Files download only once a card scrolls near the viewport (lazy sources),
     then play muted while centred and pause when out of view. Tapping toggles
     play/pause. External URLs build their iframe on first tap.
     --------------------------------------------------------------------- */
  function initReels() {
    document.querySelectorAll('[data-ak-reels]').forEach(function (rail) {
      if (rail.dataset.reelsReady === 'true') return;
      rail.dataset.reelsReady = 'true';

      var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-ak-reel]'));

      cards.forEach(function (frame) {
        var reel = frame.closest('.ak-reel');
        var video = frame.querySelector('video');
        var toggle = frame.querySelector('[data-ak-reel-toggle]');
        var embedUrl = frame.getAttribute('data-ak-reel-embed');
        var mount = frame.querySelector('[data-ak-reel-embed-mount]');
        var userPaused = false;

        if (video) {
          video.muted = true;
          video.preload = 'none';
          video.setAttribute('playsinline', '');
          video.pause();
        }

        function play() {
          if (video) {
            video.play().catch(function () {});
            if (reel) reel.classList.add('is-playing');
          }
        }

        function pause() {
          if (video) {
            video.pause();
            if (reel) reel.classList.remove('is-playing');
          }
        }

        function buildEmbed() {
          if (!embedUrl || !mount || mount.dataset.built === 'true') return;
          mount.dataset.built = 'true';
          var iframe = document.createElement('iframe');
          var sep = embedUrl.indexOf('?') === -1 ? '?' : '&';
          iframe.src = embedUrl + sep + 'autoplay=1&muted=1&playsinline=1&rel=0';
          iframe.loading = 'lazy';
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.title = 'Video';
          mount.removeAttribute('hidden');
          mount.appendChild(iframe);
        }

        if (toggle) {
          toggle.addEventListener('click', function () {
            if (video) {
              if (video.paused) {
                userPaused = false;
                play();
              } else {
                userPaused = true;
                pause();
              }
            } else if (embedUrl) {
              buildEmbed();
              if (reel) reel.classList.add('is-playing');
            }
          });
        }

        if (typeof IntersectionObserver === 'undefined') return;

        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                if (video && video.preload === 'none') {
                  video.preload = 'metadata';
                  video.load();
                }
                if (entry.intersectionRatio >= 0.6 && !userPaused) play();
              } else {
                pause();
              }
            });
          },
          { threshold: [0, 0.6, 1] }
        );
        io.observe(frame);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Offer link click tracking
     Every Diwali pack-page placement carries data-ak-offer="<place>"
     (drawer, announcement, search, pdp, collection, cart, notes...).
     Pushes a single dataLayer event per click so GA4 / GTM can measure
     which placement converts. Passive listener; never blocks navigation.
     --------------------------------------------------------------------- */
  function initOfferTracking() {
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var anchor = target.closest('[data-ak-offer]');
        if (!anchor) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'akmas_offer_click',
          place: anchor.getAttribute('data-ak-offer') || '',
          url: anchor.getAttribute('href') || ''
        });
      },
      { passive: true }
    );
  }

  /* ---------------------------------------------------------------------
     Scent-note chips on the product card
     The Liquid renders the first chip plus a "+N" chip. If the first chip is
     still too long at 130% / 150% text, the row is measured and the chip is
     replaced by "+N" (the real number of notes) rather than wrapping or
     clipping. Nothing wraps and "+N" always reports the real count.
     --------------------------------------------------------------------- */
  function fitCardNotes() {
    var lists = document.querySelectorAll('[data-ak-card-notes]');
    if (!lists.length) return;

    lists.forEach(function (list) {
      var chips = [];
      var more = null;
      Array.prototype.forEach.call(list.children, function (el) {
        if (el.hasAttribute('data-ak-notes-more')) more = el;
        else chips.push(el);
      });

      var total = parseInt(list.getAttribute('data-ak-notes-total'), 10) || chips.length;

      chips.forEach(function (chip) {
        chip.hidden = false;
      });
      if (more) more.hidden = true;

      var shown = chips.length;
      var hidden = Math.max(0, total - shown);

      /* Shrink from the end until the row fits, counting for the "+N" chip
         that has to fit beside the remaining ones. */
      for (;;) {
        if (more) {
          more.hidden = hidden <= 0;
          if (hidden > 0) more.textContent = '+' + hidden;
        }
        if (list.scrollWidth <= list.clientWidth + 1) break;
        if (shown <= 0) break;
        shown -= 1;
        chips[shown].hidden = true;
        hidden = Math.max(0, total - shown);
      }
    });
  }

  /* ---------------------------------------------------------------------
     Sticky Diwali box bar (homepage, mobile only)
     Hidden until the shopper has scrolled past the hero so the hero owns the
     first screen, then slides up. Dismissing hides it for the rest of the
     session and gives the page its space back. The menu drawer already
     suppresses it via .ak-drawer-open .ak-boxbar in the CSS.
     --------------------------------------------------------------------- */
  function initBoxbar() {
    var STORAGE_KEY = 'ak-boxbar-dismissed';

    document.querySelectorAll('[data-ak-boxbar]').forEach(function (bar) {
      if (bar.dataset.akBoxbarReady === 'true') return;
      bar.dataset.akBoxbarReady = 'true';

      try {
        if (window.sessionStorage.getItem(STORAGE_KEY) === '1') {
          bar.classList.add('is-dismissed');
          return;
        }
      } catch (err) {
        /* sessionStorage can throw in private mode; treat as not dismissed */
      }

      var hero = document.querySelector('.ak-hero');
      var ticking = false;

      function update() {
        ticking = false;
        var threshold = hero ? hero.offsetTop + hero.offsetHeight - 8 : 240;
        bar.classList.toggle('is-visible', window.scrollY > threshold);
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      var close = bar.querySelector('[data-ak-boxbar-close]');
      if (close) {
        close.addEventListener('click', function () {
          bar.classList.remove('is-visible');
          bar.classList.add('is-dismissed');
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          try {
            window.sessionStorage.setItem(STORAGE_KEY, '1');
          } catch (err) {
            /* ignore: the bar is already hidden for this page view */
          }
        });
      }

      update();
    });
  }

  /* ---------------------------------------------------------------------
     Diwali offer ladder, live progress
     The flow snippets/akmas-offer-ladder.liquid renders the ladder complete
     and honest without JS (step 1, "Pick 3 perfumes and save ..."). When JS
     is on we read /cart.js, count the perfumes (soaps do not count), fill the
     line to the reached step, tick reached steps and write one plain line:
       empty  → "Pick 3 perfumes and save <save3>"
       1      → "Add 1 more perfume to pay <price2> for 2"
       2      → "Add 1 more perfume to pay <price3> for 3"
       3+     → "You unlocked the best deal"
     Recomputes on cart-line changes and on window load; a single fetch,
     coalesced, so several ladders (home, category, drawer) never race.
     --------------------------------------------------------------------- */
  var ladderMoney = function (cents) {
    var value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: (window.AkmasOffers && window.AkmasOffers.currency) || (window.AkmasCartConfig && window.AkmasCartConfig.currency) || 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);
    } catch (err) {
      return '\u20B9' + value.toFixed(0);
    }
  };

  function ladderPrices(ladder) {
    var raw = ladder.getAttribute('data-ak-ladder-prices') || '';
    var parts = raw.split(',').map(function (n) { return Number(n) || 0; });
    return { one: parts[0] || 0, two: parts[1] || 0, three: parts[2] || 0 };
  }

  function ladderCount(cart) {
    var soapIds = {};
    var gift = (window.AkmasOffers && window.AkmasOffers.gift) || {};
    (gift.variants || []).forEach(function (variant) {
      soapIds[String(variant.id)] = true;
    });
    return (cart.items || []).reduce(function (sum, item) {
      if (soapIds[String(item.variant_id)]) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function renderLadder(ladder, count) {
    var prices = ladderPrices(ladder);
    var stage = Math.max(1, Math.min(count, 3));
    ladder.setAttribute('data-ak-stage', String(stage));

    var line = ladder.querySelector('[data-ak-ladder-line]');
    var status = ladder.querySelector('[data-ak-ladder-status]');
    var save3 = prices.one * 3 - prices.three;

    var text;
    if (count <= 0) {
      text = 'Pick 3 perfumes and save ' + ladderMoney(save3);
    } else if (count === 1) {
      text = 'Add 1 more perfume to pay ' + ladderMoney(prices.two) + ' for 2';
    } else if (count === 2) {
      text = 'Add 1 more perfume to pay ' + ladderMoney(prices.three) + ' for 3';
    } else {
      text = 'You unlocked the best deal';
    }

    if (line) line.textContent = text;
    if (status) {
      status.textContent = 'Step ' + stage + ' of 3 reached';
    }
  }

  function initOfferLadders() {
    var lastCart = null;

    function renderAll() {
      var count = lastCart ? ladderCount(lastCart) : null;
      var stage = count == null ? null : Math.max(1, Math.min(count, 3));
      document.querySelectorAll('[data-ak-ladder]').forEach(function (ladder) {
        if (lastCart) {
          renderLadder(ladder, count);
        } else if (stage == null) {
          ladder.setAttribute('data-ak-stage', ladder.getAttribute('data-ak-stage') || '1');
        }
      });
    }

    function update() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          lastCart = cart;
          renderAll();
        })
        .catch(function () {
          /* keep the server-rendered step 1 line (theme.js runs on every page,
             and /cart.js is same-origin, so this is only a safety net) */
        });
    }

    if (document.querySelector('[data-ak-ladder]')) update();

    /* Fired by assets/akmas-cart.js with the fresh payload whenever the cart
       changes (add, remove, quantity, gift sync) and again on load. Re-queries
       the DOM first, so ladders injected later (the cart drawer) are caught. */
    window.addEventListener('akmas:cart-updated', function (event) {
      if (event && event.detail) {
        lastCart = event.detail;
        renderAll();
      } else {
        update();
      }
    });
    window.addEventListener('load', update);
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init() {
    initOfferTracking();
    initOfferLadders();
    initAnnouncement();
    initStickyShadow();
    initMegaMenu();
    initDrawer();
    initCarousel();
    initFooterAccordions();
    initCardAdd();
    initTabs();
    initRail();
    initReels();
    fitCardNotes();
    initBoxbar();
  }

  /* Chip fitting depends on rendered text width, so re-measure whenever the
     viewport or the loaded webfonts change. Registered once at module scope so
     section re-loads in the theme editor do not stack listeners. */
  var fitNotesTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(fitNotesTimer);
    fitNotesTimer = window.setTimeout(fitCardNotes, 150);
  });
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(function () {
      fitCardNotes();
    });
  }
  window.addEventListener('load', fitCardNotes, { once: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', function () {
    init();
    fitCardNotes();
  });
})();
